import React from 'react';
import { Camera, Menu, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMemberSince as sharedFormatMemberSince } from '../utils/dataConsistency';
import './AccountTopHeader.css';

/**
 * Premium Account Top Header Component
 * Features a crisp curved wave separator, centered avatar, and wallet number copy
 * Fixed: No fuzzy edges in RTL, crisp SVG rendering
 */
export default function AccountTopHeader({
  coverColor = "#1F6F3A",
  name = "User Name",
  email = "email@example.com",
  walletNumber = null,
  avatarUrl = null,
  avatarFallback = null,
  createdAt = null,
  onEditPhoto = () => {},
  onMenuClick = () => {},
  onCopy = () => {},
  copied = false,
  isRTL = false,
  isLoading = false,
}) {
  const { t } = useTranslation();
  // Broken avatar URLs must never paint alt text inside the circle — fall
  // back to the initials placeholder instead. Reset when the URL changes.
  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => { setImgFailed(false); }, [avatarUrl]);
  const showImage = !!avatarUrl && !imgFailed;
  const displaySubtitle = walletNumber ? `Wallet: ${walletNumber}` : email;
  
  // Use shared formatMemberSince — ONE source of truth
  const locale = isRTL ? 'ar' : 'en';
  const memberSinceDate = sharedFormatMemberSince(createdAt, locale);
  
  return (
    <div className={`account-top-header ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Cover Section with Crisp Wave - No blur/glow */}
      <div className="ath-cover" style={{ background: 'linear-gradient(180deg, #1f7a3f 0%, #2e8b57 100%)' }}>
        {/* Menu Button */}
        <div className="ath-menu-area">
          <button 
            className="ath-menu-btn" 
            onClick={onMenuClick}
            aria-label="Menu"
            data-testid="menu-btn"
          >
            <Menu size={18} />
          </button>
        </div>

        {/* Crisp Curved Wave Separator - Fixed for RTL */}
        <div className="ath-curve-wrap" aria-hidden="true">
          <svg
            className="ath-curve-svg"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
          >
            {/* Top green section — subtle curve */}
            <path
              d="M0,0H1440V180C1200,210,900,230,720,220C520,208,300,190,0,200Z"
              fill={coverColor}
              shapeRendering="geometricPrecision"
            />
            {/* White wave cut — gentle transition */}
            <path
              d="M0,200C300,190,520,208,720,220C900,230,1200,210,1440,180V320H0Z"
              fill="var(--ath-bg-color, #F6F8F7)"
              shapeRendering="geometricPrecision"
            />
          </svg>
        </div>
      </div>

      {/* Profile Content - Overlaps the wave */}
      <div className="ath-profile-content">
        <div className="ath-center-col">
          {/* Avatar */}
          <div className="ath-avatar-wrap">
            {showImage ? (
              <div className="ath-avatar-imgwrap">
                <img
                  src={avatarUrl}
                  alt=""
                  className="ath-avatar-img"
                  draggable={false}
                  onError={() => setImgFailed(true)}
                  data-testid="account-avatar-img"
                />
                {isLoading && (
                  <div className="ath-avatar-overlay" data-testid="avatar-uploading-overlay">
                    <div className="ath-spinner" />
                  </div>
                )}
              </div>
            ) : isLoading ? (
              <div className="ath-avatar-loading">
                <div className="ath-spinner" />
              </div>
            ) : (
              <div className="ath-avatar-placeholder">
                {avatarFallback || (
                  <span className="ath-avatar-icon">👤</span>
                )}
              </div>
            )}

            {/* Camera/Edit Button */}
            <button
              onClick={onEditPhoto}
              className="ath-camera-btn"
              aria-label="Change photo"
              title="Change photo"
              data-testid="avatar-edit-btn"
            >
              <Camera size={14} />
            </button>
          </div>

          {/* Name */}
          <h1 className="ath-name">{name}</h1>

          {/* Member Since - Premium styled */}
          {memberSinceDate && (
            <div className="ath-member-since" role="note" aria-label="member-since">
              <span className="ath-member-since-dot" aria-hidden="true"></span>
              <span className="ath-member-since-text">
                {t('account.memberSince', { date: memberSinceDate })}
              </span>
            </div>
          )}

          {/* Wallet/Email Row with Copy */}
          <div className="ath-subtitle-row">
            <span className="ath-subtitle-text">{displaySubtitle}</span>
            <button 
              onClick={onCopy} 
              className="ath-copy-btn" 
              aria-label="Copy"
              data-testid="copy-wallet-btn"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>

          {/* Secondary line (email if wallet is shown) */}
          {walletNumber && email && (
            <p className="ath-secondary">{email}</p>
          )}
        </div>
      </div>
    </div>
  );
}
